import React, { useState } from 'react';
import logo from 'images/filmik-horizontal-logo-black.svg'; 
import Footer from 'components/shared/Footer';

interface VerificationMethodSelectProps {
  email?: string;
  phone?: string;
  onSuccess: (method: 'sms' | 'email') => void;
  onError?: (error: string) => void;
  isLoading?: boolean;
  error?: string;
}

export const VerificationMethodSelect: React.FC<VerificationMethodSelectProps> = ({
  email,
  phone,
  onSuccess,
  onError,
  isLoading = false,
  error: externalError = ''
}) => {
  const [selectedMethod, setSelectedMethod] = useState<'sms' | 'email'>(
    phone ? 'sms' : 'email'
  );
  const [internalError, setInternalError] = useState<string>('');

  const error = externalError || internalError;

  const getCSRFToken = (): string => {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    return token || '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInternalError('');

    // Validate that the selected method has corresponding contact info
    if (selectedMethod === 'sms' && !phone) {
      const errorMessage = 'Phone number not available for SMS verification';
      setInternalError(errorMessage);
      onError?.(errorMessage);
      return;
    }

    if (selectedMethod === 'email' && !email) {
      const errorMessage = 'Email address not available for email verification';
      setInternalError(errorMessage);
      onError?.(errorMessage);
      return;
    }

    onSuccess(selectedMethod);
  };

  // Don't render if no contact methods are available
  if (!email && !phone) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <div className="flex-grow flex items-center justify-center px-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-md p-8">
            <div className="flex justify-center mb-6">
              <img src="/assets/filmik-horizontal-logo-black.svg" alt="FILMIK" className="h-8" />
            </div>
            <h2 className="text-center text-2xl font-bold text-black mb-2">No Verification Methods Available</h2>
            <p className="text-center text-gray-600">
              Please contact support to set up verification methods.
            </p>
          </div>
        </div>
        <Footer className="w-full" />
      </div>
    );
  }

 return (
  <div className="min-h-screen flex flex-col bg-gray-50">
    <div className="flex-grow flex items-center justify-center px-4">
      <div className="bg-white w-full max-w-md rounded-xl shadow-md p-8">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img src={logo} alt="Filmik Logo" className="h-8"/>
        </div>
        
        {/* Title */}
        <h2 className="text-center text-2xl font-bold text-black mb-2">Verify Your Account</h2>
        <p className="text-center text-gray-600 mb-6">
          For your security, we need to verify your identity
        </p>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">

            {email && (
              <label className={`block cursor-pointer rounded-lg border p-4 bg-yellow-50 border-yellow-200 ${selectedMethod === 'email' ? 'ring-2 ring-blue-500' : ''}`}>
                <div className="flex items-start">
                  <input
                    type="radio"
                    name="method"
                    value="email"
                    checked={selectedMethod === 'email'}
                    onChange={() => setSelectedMethod('email')}
                    className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500"
                    disabled={isLoading}
                  />
                  <div className="ml-3">
                    <p className="font-semibold text-gray-900">Verify via Email</p>
                    <p className="text-sm text-gray-600">
                      We'll send a code to your registered email address
                    </p>
                  </div>
                </div>
              </label>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full mt-6 rounded-md py-2 text-white font-semibold text-sm transition ${
              isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isLoading ? 'Sending...' : 'Send Code'}
          </button>
        </form>
      </div>
    </div>
    {/* <Footer className="w-full" /> */}
  </div>
  );
};